import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  demoModeAtom,
  effectiveAccessRightsAtom,
  userDataAtom,
} from '@/global-store';
import type { AccessLevel, TeamProductType } from '@eye-able/types';

/**
 * Returns a stable resolver `(licenseId) => boolean` for a product type. Use it
 * where the license id varies per element (e.g. table rows spanning several
 * licenses) and a per-row hook call is not possible.
 *
 * The resolution order mirrors the backend's `assertCanEditLicense`:
 *   1. demo mode            -> editable
 *   2. admin / sales admin  -> editable (bypass all checks)
 *   3. rights not loaded yet
 *      or no license id      -> `optimistic` (backend still enforces)
 *   4. license not covered   -> editable ("uncovered / legacy = editor")
 *   5. otherwise             -> editable only when the level is `editor`
 *
 * `optimistic` (default `true`) controls step 3: keep it `true` for UX gating
 * (avoid flicker / false negatives during load), but pass `false` for
 * background mutations so we don't fire silently-failing 403 requests for
 * viewers before their rights have loaded.
 */
export function useLicenseEditResolver(
  productType: TeamProductType,
  optimistic = true,
): (licenseId: number | null | undefined) => boolean {
  const demoMode = useAtomValue(demoModeAtom);
  const user = useAtomValue(userDataAtom);
  const effectiveRights = useAtomValue(effectiveAccessRightsAtom);

  const levelByLicenseId = useMemo(() => {
    const map = new Map<number, AccessLevel>();
    for (const right of effectiveRights?.[productType] ?? []) {
      map.set(right.licenseId, right.accessLevel);
    }
    return map;
  }, [effectiveRights, productType]);

  return useCallback(
    (licenseId: number | null | undefined) => {
      if (demoMode) return true;
      if (user?.isAdmin || user?.isSalesAdmin) return true;
      if (!effectiveRights || licenseId == null) return optimistic;

      const level = levelByLicenseId.get(licenseId);
      if (!level) return true;

      return level === 'editor';
    },
    [demoMode, user, effectiveRights, levelByLicenseId, optimistic],
  );
}

/**
 * Whether the current user may edit a given license, mirroring the backend's
 * `assertCanEditLicense` so the UI can disable edit controls instead of letting
 * a viewer click "save" and get a 403.
 *
 * The backend remains the source of truth; this only drives UX. The resolution
 * order matches the backend exactly:
 *   1. demo mode            -> editable (no real user / enforcement)
 *   2. admin / sales admin  -> editable (they bypass all checks)
 *   3. rights not loaded yet
 *      or no license id      -> `optimistic` (backend still enforces)
 *   4. license not covered   -> editable ("uncovered / legacy = editor")
 *   5. otherwise             -> editable only when the level is `editor`
 *
 * Pass `optimistic: false` for background mutations so we don't fire
 * silently-failing 403 requests for viewers before their rights have loaded.
 *
 * Note: translate / plain-language edits are gated through the `moduleAccesses`
 * product type (the `translate` / `plainLanguage` module-access flags), since
 * the standalone `translate` license type is not part of `TeamProductType`.
 */
export function useCanEditLicense(
  productType: TeamProductType,
  licenseId: number | null | undefined,
  optimistic = true,
): boolean {
  const resolveCanEdit = useLicenseEditResolver(productType, optimistic);
  return resolveCanEdit(licenseId);
}
